import { Injectable, inject } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { AuthService } from 'src/app/auth/services/auth.service';
import { patch, removeItem, append } from '@ngxs/store/operators';
import { tap, Observable, combineLatestWith, finalize, of, map } from 'rxjs';
import { USER_ROLES } from '../enums/UserRoles.enum';
import { IPersistentFilter } from '../interfaces/employee/IPersistentFilter';
import { IUser } from '../interfaces/IUser';
import { IImpersonation } from '../interfaces/IImpersonation';
import { IImpersonationModel } from '../interfaces/IImpersonationModel';
import { ImpersonationService } from '../services/impersonation.service';
import {
  userImpersonatedNotification,
  userImpersonationStoppedNotification,
  userImpersonationFailedNotification,
} from '../configs/impersonation.config';
import { NotificationService } from '../services/notification.service';
import { EmployeeService } from '../services/employee.service';
import { IEmployeeListItem } from '../interfaces/IEmployeeListItem';
import { IUserStateModel } from '../interfaces/IUserStateModel';
import {
  SetUser,
  Logout,
  StopUserImpersonation,
  StartUserImpersonation,
  Login,
  LoadUserImpersonationData,
  UpdateImpersonations,
  LoadPersistentFilters,
  UpdatePersistentFilters,
  LoadImitableUsers,
  LoadImpersonatingUsers,
} from './user.actions';
import * as Sentry from '@sentry/angular';

@State<IUserStateModel>({
  name: 'userState',
  defaults: {
    user: {
      employeeId: null,
      firstname: null,
      lastname: null,
      gid: null,
      isUserAdmin: false,
      isMemberOfServiceLanePool: false,
      isTimekeeper: false,
      role: null,
      orgTeam: null,
      team: null,
      lastApprovedMonth: null,
      lastMonthlySettlement: null,
      scdEMail: null,
      scdName: null,
      location: null,
      country: null,
      contractTypeName: null,
      workingHoursHaveTo: 0,
    },
    filters: {
      filterGroup: null,
      filterPartner: null,
      filterTeam: null,
      accountingMonth: null,
      lastRunDate: null,
    },
    isAuthenticated: false,
    impersonation: {
      currentImpersonation: null,
      impersonatingUsers: [],
      imitableUsers: [],
    },
    isLoading: false,
    isUserPersistentFilterLoading: false,
  },
})
@Injectable()
export class UserState {
  private authService = inject(AuthService);
  private impersonationService = inject(ImpersonationService);
  private notificationService = inject(NotificationService);
  private employeeService = inject(EmployeeService);

  @Selector()
  static userIsLoaded(state: IUserStateModel): boolean {
    return !!state.user.employeeId;
  }

  @Selector()
  static user(state: IUserStateModel): IUser {
    return state.user;
  }

  @Selector()
  static isAdmin(state: IUserStateModel): boolean {
    return state.user.isUserAdmin;
  }

  @Selector()
  static isMemberOfServiceLanePool(state: IUserStateModel): boolean {
    return state.user.isMemberOfServiceLanePool;
  }

  @Selector()
  static userRole(state: IUserStateModel): USER_ROLES {
    return state.user.role;
  }

  @Selector()
  static userCountry(state: IUserStateModel): string {
    return state.user.country;
  }

  @Selector()
  static userContractTypeName(state: IUserStateModel): string {
    return state.user.contractTypeName;
  }

  @Selector()
  static userHasTvInOrgChart(state: IUserStateModel): boolean {
    return state.user.hasTvInOrgChart ?? false;
  }

  @Selector()
  static filters(state: IUserStateModel): IPersistentFilter {
    return state.filters;
  }

  @Selector()
  static accountingMonth(state: IUserStateModel): Date {
    return state.filters.accountingMonth ? new Date(state.filters.accountingMonth) : null;
  }

  @Selector()
  static isPreviewAvailable(state: IUserStateModel): boolean {
    if (!state.filters.accountingMonth) {
      return false;
    }
    const accountingMonth = new Date(state.filters.accountingMonth);
    const now = new Date();
    return (
      now.getFullYear() > accountingMonth.getFullYear() ||
      (now.getFullYear() === accountingMonth.getFullYear() && now.getMonth() > accountingMonth.getMonth())
    );
  }

  @Selector()
  static impersonation(state: IUserStateModel): IImpersonationModel {
    return state.impersonation;
  }

  @Selector()
  static impersonatingUsers(state: IUserStateModel): IImpersonation[] {
    return state.impersonation.impersonatingUsers;
  }

  @Selector()
  static imitableUsers(state: IUserStateModel): IImpersonation[] {
    return state.impersonation.imitableUsers;
  }

  @Selector()
  static currentImpersonation(state: IUserStateModel): IImpersonation {
    return state.impersonation.currentImpersonation;
  }

  @Selector()
  static isLoading(state: IUserStateModel): boolean {
    return state.isLoading;
  }

  @Selector()
  static isUserPersistentFilterLoading(state: IUserStateModel): boolean {
    return state.isUserPersistentFilterLoading;
  }

  @Action(SetUser)
  setUser(ctx: StateContext<IUserStateModel>, { payload }: SetUser): Observable<IEmployeeListItem> {
    this.setIsLoading(ctx, true);
    return this.employeeService.getEmployeeByGID(payload.gid).pipe(
      tap(userListItem => {
        const user = {
          employeeId: payload.employeeId,
          firstname: payload.firstname,
          gid: payload.gid,
          lastname: payload.lastname,
          isUserAdmin: payload.isUserAdmin,
          isMemberOfServiceLanePool: payload.isMemberOfServiceLanePool,
          isTimekeeper: userListItem.isTimekeeper,
          role: payload.role,
          orgTeam: payload.orgTeam,
          team: payload.team,
          lastApprovedMonth: userListItem.lastApprovedMonth,
          lastMonthlySettlement: userListItem.lastMonthlySettlement,
          scdEMail: payload.scdEmail,
          scdName: payload.scdName,
          workingHoursHaveTo: userListItem.workingHoursHaveTo,
          location: userListItem.location,
          country: userListItem.country,
          contractTypeName: userListItem.contractTypeName,
          hasTvInOrgChart: userListItem.hasTvInOrgChart,
        };
        Sentry.setUser({ email: user.scdEMail, id: user.gid, username: `${user.firstname} ${user.lastname}` });
        ctx.patchState({ user });
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(Logout)
  logout(): void {
    this.authService.logout();
  }

  @Action(Login)
  login(): void {
    this.authService.login();
  }

  @Action(StopUserImpersonation)
  stopUserImpersonation(ctx: StateContext<IUserStateModel>): Observable<null> {
    this.authService.setImpersonatingUser(null);
    this.authService.updateAuthState();

    ctx.setState(
      patch<IUserStateModel>({
        impersonation: patch<IUserStateModel['impersonation']>({
          currentImpersonation: null,
        }),
      }),
    );
    this.notificationService.showMessage(userImpersonationStoppedNotification);
    return of(null);
  }

  @Action(StartUserImpersonation)
  startUserImpersonation(ctx: StateContext<IUserStateModel>, action: StartUserImpersonation): Observable<null> {
    return this.impersonationService.impersonateUser(action.imitableUser).pipe(
      tap(data => {
        if (data) {
          ctx.setState(
            patch<IUserStateModel>({
              impersonation: patch<IUserStateModel['impersonation']>({
                currentImpersonation: action.imitableUser,
              }),
            }),
          );

          if (data != null) {
            this.authService.setImpersonatingUser(action.imitableUser.parentGid);
            this.authService.updateAuthState();
            const notificationData: object = {
              user: action.imitableUser.parentName,
            };
            this.notificationService.showMessage(userImpersonatedNotification, notificationData);
          } else {
            this.authService.setImpersonatingUser(null);
            this.authService.updateAuthState();
            const notificationData: object = {
              user: action.imitableUser.parentName,
            };
            this.notificationService.showMessage(userImpersonationFailedNotification, notificationData);
          }
        }
      }),
      map((): null => null),
    );
  }

  @Action(UpdateImpersonations)
  updateImpersonations(ctx: StateContext<IUserStateModel>, action: UpdateImpersonations): void {
    action.dataPipe.deletableImpersonations.forEach(impersonation => this.impersonationService.deleteImpersonatingUser(impersonation));
    this.removeImpersonations(ctx, action);

    action.dataPipe.addableImpersonations.forEach(impersonation => this.impersonationService.addImpersonation(impersonation));
    this.addImpersonations(ctx, action);
  }

  private addImpersonations(ctx: StateContext<IUserStateModel>, action: UpdateImpersonations): void {
    ctx.setState(
      patch<IUserStateModel>({
        impersonation: patch<IUserStateModel['impersonation']>({
          impersonatingUsers: append<IImpersonation>(action.dataPipe.addableImpersonations),
        }),
      }),
    );
  }

  private removeImpersonations(ctx: StateContext<IUserStateModel>, action: UpdateImpersonations): void {
    ctx.setState(
      patch<IUserStateModel>({
        impersonation: patch<IUserStateModel['impersonation']>({
          impersonatingUsers: removeItem<IImpersonation>(element =>
            action.dataPipe.deletableImpersonations.map(item => item.parentGid).includes(element.parentGid),
          ),
        }),
      }),
    );
  }

  @Action(LoadUserImpersonationData)
  loadUserImpersonationData(ctx: StateContext<IUserStateModel>): Observable<IImpersonation[][]> {
    this.setIsLoading(ctx, true);
    const impersonatingUsers$ = this.impersonationService.getImpersonatingUsers();
    const imitableUsers$ = this.impersonationService.getImitableUsers(ctx.getState().user.gid);

    return impersonatingUsers$.pipe(
      combineLatestWith(imitableUsers$),
      tap(([impersonatingUsers, imitableUsers]) => {
        ctx.setState(
          patch<IUserStateModel>({
            impersonation: patch<IUserStateModel['impersonation']>({
              imitableUsers,
              impersonatingUsers,
            }),
          }),
        );
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadImitableUsers)
  loadImitableUsers(ctx: StateContext<IUserStateModel>): Observable<IImpersonation[]> {
    this.setIsLoading(ctx, true);
    return this.impersonationService.getImitableUsers(ctx.getState().user.gid).pipe(
      tap(imitableUsers => {
        ctx.setState(
          patch<IUserStateModel>({
            impersonation: patch<IUserStateModel['impersonation']>({
              imitableUsers,
            }),
          }),
        );
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadImpersonatingUsers)
  loadImpersonatingUsers(ctx: StateContext<IUserStateModel>, { gid }: LoadImpersonatingUsers): Observable<IImpersonation[]> {
    this.setIsLoading(ctx, true);
    return this.impersonationService.getImpersonatingUsers(gid).pipe(
      tap(impersonatingUsers => {
        ctx.setState(
          patch<IUserStateModel>({
            impersonation: patch<IUserStateModel['impersonation']>({
              impersonatingUsers,
            }),
          }),
        );
      }),
      finalize(() => this.setIsLoading(ctx, false)),
    );
  }

  @Action(LoadPersistentFilters)
  loadPersistentFilters(ctx: StateContext<IUserStateModel>): Observable<IPersistentFilter> {
    this.setIsUserPersistentFilterLoading(ctx, true);
    return this.employeeService.getPersistentFilter(ctx.getState().user.gid).pipe(
      tap((filter: IPersistentFilter) => {
        ctx.patchState({
          filters: filter,
        });
      }),
      finalize(() => this.setIsUserPersistentFilterLoading(ctx, false)),
    );
  }

  @Action(UpdatePersistentFilters)
  updatePersistentFilters(ctx: StateContext<IUserStateModel>, action: UpdatePersistentFilters): Observable<void> {
    this.setIsUserPersistentFilterLoading(ctx, true);
    return this.employeeService.updatePersistentFilter(action.persistentFilterBody).pipe(
      tap(() => {
        ctx.patchState({
          filters: {
            ...ctx.getState().filters,
            [action.filterPropertyToUpdate]: action.persistentFilterBody.filterView[action.filterPropertyToUpdate],
          },
        });
      }),
      finalize(() => this.setIsUserPersistentFilterLoading(ctx, false)),
    );
  }

  private setIsLoading(ctx: StateContext<IUserStateModel>, isLoading: boolean): void {
    ctx.patchState({ isLoading });
  }

  private setIsUserPersistentFilterLoading(ctx: StateContext<IUserStateModel>, isUserPersistentFilterLoading: boolean): void {
    ctx.patchState({ isUserPersistentFilterLoading });
  }
}
